import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Lottie, { type LottieRefCurrentProps } from 'lottie-react';

import type { SceneAnchor } from './engine/types';
import type { FlowThemeBursts, FlowThemeDefinition } from './flowThemeRegistry';

export type FlowThemeBurstKind = 'step' | 'finale';

export interface FlowThemeBurstHandle {
  play: (kind: FlowThemeBurstKind, anchor: SceneAnchor | null) => void;
  /** Keeps a playing burst pinned to a moving anchor. */
  follow: (anchor: SceneAnchor | null) => void;
}

interface FlowThemeBurstProps {
  theme: FlowThemeDefinition;
}

const FINALE_SCALE = 1.6;

/**
 * Lottie celebrations layered over a theme scene: one burst each time the
 * learner reaches a step, and a bigger one when they finish.
 */
export const FlowThemeBurst = forwardRef<FlowThemeBurstHandle, FlowThemeBurstProps>(({ theme }, ref) => {
  const [bursts, setBursts] = useState<FlowThemeBursts | null>(null);
  const stepRef = useRef<LottieRefCurrentProps | null>(null);
  const finaleRef = useRef<LottieRefCurrentProps | null>(null);
  const stepBoxRef = useRef<HTMLDivElement | null>(null);
  const finaleBoxRef = useRef<HTMLDivElement | null>(null);
  const activeRef = useRef<{ step: boolean; finale: boolean }>({ step: false, finale: false });

  useEffect(() => {
    let active = true;
    theme.loadBursts?.()
      .then((loaded) => {
        if (active) {
          setBursts(loaded);
        }
      })
      .catch((error) => {
        console.warn('Unable to load the flow theme bursts', error);
      });
    return () => {
      active = false;
    };
  }, [theme]);

  const place = useCallback((box: HTMLDivElement | null, anchor: SceneAnchor | null, scale: number) => {
    if (!box) {
      return;
    }
    if (!anchor) {
      box.style.opacity = '0';
      return;
    }
    const size = theme.burstSize * anchor.scale * scale;
    box.style.width = `${size}px`;
    box.style.height = `${size}px`;
    box.style.transform = `translate3d(${anchor.x - size / 2}px, ${anchor.y - size / 2}px, 0)`;
  }, [theme.burstSize]);

  useImperativeHandle(ref, () => ({
    play: (kind, anchor) => {
      const box = kind === 'step' ? stepBoxRef.current : finaleBoxRef.current;
      const player = kind === 'step' ? stepRef.current : finaleRef.current;
      if (!box || !player || !anchor) {
        return;
      }
      activeRef.current[kind] = true;
      place(box, anchor, kind === 'finale' ? FINALE_SCALE : 1);
      box.style.opacity = '1';
      player.goToAndPlay(0, true);
    },
    follow: (anchor) => {
      if (activeRef.current.step) {
        place(stepBoxRef.current, anchor, 1);
      }
      if (activeRef.current.finale) {
        place(finaleBoxRef.current, anchor, FINALE_SCALE);
      }
    },
  }), [place]);

  const finish = (kind: FlowThemeBurstKind) => () => {
    activeRef.current[kind] = false;
    const box = kind === 'step' ? stepBoxRef.current : finaleBoxRef.current;
    if (box) {
      box.style.opacity = '0';
    }
  };

  if (!bursts) {
    return null;
  }

  return (
    <>
      <div ref={stepBoxRef} className="flow-theme-burst" data-testid="flow-theme-burst-step">
        <Lottie
          lottieRef={stepRef}
          animationData={bursts.step}
          autoplay={false}
          loop={false}
          onComplete={finish('step')}
          className="h-full w-full"
        />
      </div>
      <div ref={finaleBoxRef} className="flow-theme-burst" data-testid="flow-theme-burst-finale">
        <Lottie
          lottieRef={finaleRef}
          animationData={bursts.finale}
          autoplay={false}
          loop={false}
          onComplete={finish('finale')}
          className="h-full w-full"
        />
      </div>
    </>
  );
});

FlowThemeBurst.displayName = 'FlowThemeBurst';
